import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { merge, Observable, of, Subscription, timer } from 'rxjs';
import { delay, finalize, map, switchMap } from 'rxjs/operators';
import { USER_ROLES } from 'src/app/core/constants/cms-user-roles';
import { IAuthor } from 'src/app/core/models/i-author';
import { AlertService } from 'src/app/shared/services/alert.service';
import { AuthorService } from 'src/app/shared/services/author.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';

@Component({
  selector: 'app-edit-author-management',
  templateUrl: './edit-author-management.component.html',
})
export class EditAuthorManagementComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private alertService: AlertService,
    private userRolesService: UserRolesService,
    public dialogService: DialogService
  ) {}

  subscriptions: Subscription[] = [];
  currentAuthorId: string;
  authors: IAuthor[] = [];
  author: IAuthor;
  authorNameInput = new UntypedFormControl();
  authorLocationInput = new UntypedFormControl();
  createTimerDuration = 2000;
  createTimer$: Observable<number> = timer(this.createTimerDuration);
  isUpdating = false;
  formHasChanged = false;
  characterLimitReached = false;
  isAdmin$: Observable<boolean> = of(false);

  ngOnInit(): void {
    this.currentAuthorId = this.route.snapshot.paramMap.get('id');
    this.loadAuthor();

    const valueChanges$ = merge(
      this.authorNameInput.valueChanges,
      this.authorLocationInput.valueChanges
    );

    const anyChangeSubscription = valueChanges$.subscribe(() => {
      this.formHasChanged = true;
    });

    this.isAdmin$ = this.userRolesService.isUserInRole(USER_ROLES.Admin);

    this.subscriptions.push(anyChangeSubscription);
  }

  loadAuthor(): void {
    const searchValue = this.currentAuthorId ?? '';
    const authorLoadSubscription = this.authorService
      .getById$(searchValue)
      .subscribe(
        (data) => {
          this.author = data;
          if (!this.author.location) {
            this.author.location = '';
          }
          this.populateAuthorFields();
        },
        (error) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the authors. Please contact IT.',
          });
        }
      );
    this.subscriptions.push(authorLoadSubscription);
  }

  updateAuthor(): void {
    const nameText = this.authorNameInput.value;
    const locationText = this.authorLocationInput.value;

    if (!nameText || nameText.trim() === '' || nameText.length > 200) {
      this.dialogService.openErrorDialog({
        message: 'Valid name required to create a new author.',
      });
      return;
    }

    if (locationText.length > 200) {
      this.dialogService.openErrorDialog({
        message: 'Valid location required to create a new author.',
      });
      return;
    }

    this.isUpdating = true;

    const updateAuthorSubscription = this.authorService
      .update$(this.author.id, nameText, locationText)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.isUpdating = false;
            this.alertService.success(
              'Author: ' + nameText + ' updated successfully'
            );
          }, 2000);
        })
      )
      .subscribe(
        () => {
          this.formHasChanged = false;
          this.author.name = nameText;
          this.author.location = locationText;
        },
        (error) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving author. Please contact IT.',
          });
        }
      );

    this.subscriptions.push(updateAuthorSubscription);
  }

  populateAuthorFields(): void {
    if (!this.author) {
      return;
    }

    this.authorNameInput.patchValue(this.author.name ?? '');
    this.authorLocationInput.patchValue(this.author.location ?? '');
    this.formHasChanged = false;
  }

  isSaveDisabled(): boolean {
    if (this.author) {
      this.formHasChanged = this.authorNameInput.value != this.author.name;
      if (this.authorNameInput.value.length > 200) {
        this.characterLimitReached = true;
      } else {
        this.characterLimitReached = false;
      }
    }

    if (this.author) {
      if (!this.formHasChanged) {
        this.formHasChanged =
          this.authorLocationInput.value != this.author.location;
      }
      if (
        this.authorLocationInput.value.length > 200 &&
        !this.characterLimitReached
      ) {
        this.characterLimitReached = true;
      }
    }
    return (
      this.isUpdating || !this.formHasChanged || this.characterLimitReached
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
